import React, { useState } from 'react';
import { Star, Send, CheckCircle, MessageSquareText } from 'lucide-react';
import { useAuthContext } from '../Auth/AuthContext';
import config from '../../config.json';
import './FeedbackPage.css';

const QUESTIONS = [
  { key: 'overallExperienceRating', text: 'How would you rate the overall experience of taking the BotGuru AI Prep-Interview assessment?' },
  { key: 'questionQualityRating', text: 'How would you rate the relevance and quality of the questions asked during the assessment?' },
  { key: 'improvementUsefulnessRating', text: 'How would you rate the areas of improvement suggested by BotGuru for preparing for future interviews?' },
  { key: 'improvementActionLikelihoodRating', text: 'How likely are you to work on the improvement areas suggested by BotGuru to strengthen your skills?' },
  { key: 'retakeLikelihoodRating', text: 'How likely are you to take the BotGuru Prep-Interview again after improving your skills?' },
  { key: 'recommendationLikelihoodRating', text: 'How likely are you to recommend BotGuru AI Prep-Interview to other students for interview preparation?' },
];

const StarRating = ({ value, onChange }) => {
  const [hovered, setHovered] = useState(0);

  return (
    <div className="fb-star-row">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          className={`fb-star-btn ${star <= (hovered || value) ? 'fb-star-active' : 'fb-star-inactive'}`}
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          onClick={() => onChange(star === value ? 0 : star)}
        >
          <Star
            size={28}
            fill={star <= (hovered || value) ? 'currentColor' : 'none'}
            strokeWidth={1.5}
          />
        </button>
      ))}
      {value > 0 && <span className="fb-star-label">{value}/5</span>}
    </div>
  );
};

const FeedbackPage = () => {
  const { userObjId } = useAuthContext();
  const [ratings, setRatings] = useState({});
  const [detailedReview, setDetailedReview] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleRatingChange = (key, value) => {
    setRatings((prev) => {
      const updated = { ...prev };
      if (value === 0) {
        delete updated[key];
      } else {
        updated[key] = value.toString();
      }
      return updated;
    });
  };

  const hasAnyFeedback = Object.keys(ratings).length > 0 || detailedReview.trim() !== '';

  const handleSubmit = async () => {
    if (!hasAnyFeedback) {
      setError('Please provide at least one rating or a review.');
      return;
    }

    setError('');
    setIsSubmitting(true);

    // Build payload with only filled fields
    const payload = {feedbackType: 'assessmentFeedback'};
    Object.entries(ratings).forEach(([key, val]) => {
      payload[key] = val;
    });
    if (detailedReview.trim()) {
      payload.detailedReview = detailedReview.trim();
    }

    try {
      const response = await fetch(`${config.restAPI}/accounts/submitFeedback/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitted(true);
      } else {
        setError(data.message || data.error || 'Failed to submit feedback. Please try again.');
      }
    } catch (err) {
      console.error('Feedback submit error:', err);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setRatings({});
    setDetailedReview('');
    setSubmitted(false);
    setError('');
  };

  if (submitted) {
    return (
      <div className="fb-page">
        <div className="fb-success-card">
          <div className="fb-success-icon">
            <CheckCircle size={56} />
          </div>
          <h2 className="fb-success-title">Thank You!</h2>
          <p className="fb-success-text">Your feedback has been submitted successfully. We appreciate you taking the time to help us improve.</p>
          {/* <button className="fb-submit-btn" onClick={handleReset}>Submit Another Response</button> */}
        </div>
      </div>
    );
  }

  return (
    <div className="fb-page">
      <div className="fb-header">
        <div className="fb-header-icon">
          <MessageSquareText size={28} />
        </div>
        <div>
          <h1 className="fb-title">Share Your Feedback</h1>
          <p className="fb-subtitle">Help us improve by rating your assessment experience</p>
        </div>
      </div>

      <div className="fb-card">
        <div className="fb-categories">
  {QUESTIONS.map(({ key, text }, idx) => (
    <div key={key} className="fb-category">
      <span className="fb-question-text">{idx + 1}. {text}</span>
      <StarRating value={ratings[key] ? parseInt(ratings[key]) : 0} onChange={(val) => handleRatingChange(key, val)} />
    </div>
  ))}
</div>

        <div className="fb-review-section">
          <label className="fb-review-label">Detailed Review</label>
          <p className="fb-review-hint">Share specific thoughts, suggestions, or areas for improvement</p>
          <textarea
            className="fb-review-textarea"
            placeholder="Tell us more about your experience..."
            value={detailedReview}
            onChange={(e) => setDetailedReview(e.target.value)}
            rows={5}
            maxLength={2000}
          />
          <div className="fb-review-counter">{detailedReview.length}/2000</div>
        </div>

        {error && <div className="fb-error">{error}</div>}

        <div className="fb-actions">
          <button className="fb-clear-btn" onClick={handleReset} disabled={!hasAnyFeedback || isSubmitting}>
            Clear All
          </button>
          <button className="fb-submit-btn" onClick={handleSubmit} disabled={isSubmitting || !hasAnyFeedback}>
            {isSubmitting ? (
              <>
                <div className="fb-spinner"></div>
                <span>Submitting...</span>
              </>
            ) : (
              <>
                <Send size={16} />
                <span>Submit Feedback</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeedbackPage;